import { useState } from "react"

const UpdateModal = ({ 
  isOpen, 
  onClose, 
  title = "Update Successful", 
  message = "Your information has been updated successfully.",
  icon = "checkmark",
  variant = "success",
  primaryButtonText = "Continue",
  secondaryButtonText = "Cancel",
  showSecondaryButton = false,
  onPrimaryAction,
  onSecondaryAction,
  isLoading = false,
  loadingText = "Updating...",
  customIcon,
  showCloseButton = true,
  primaryButtonBg: customPrimaryButtonBg,
  primaryButtonHover: customPrimaryButtonHover
}) => {
  const [isHovered, setIsHovered] = useState(false)

  if (!isOpen) return null

  // Get icon based on type
  const getIcon = () => {
    if (customIcon) return customIcon

    switch (icon) {
      case "checkmark":
        return (
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        )
      case "info":
        return (
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        )
      case "warning":
        return (
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
          </svg>
        )
      case "edit":
        return (
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        )
      default:
        return (
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        )
    }
  }

  // Get variant styles
  const getVariantStyles = () => {
    switch (variant) {
      case "success":
        return {
          iconBg: "#D1FAE5",
          iconColor: "#10B981",
          titleColor: "#303E4E",
          messageColor: "#6B7280",
          primaryButtonBg: customPrimaryButtonBg || "#8548A8",
          primaryButtonHover: customPrimaryButtonHover || "#7A4397"
        }
      case "info":
        return {
          iconBg: "#DBEAFE",
          iconColor: "#3B82F6",
          titleColor: "#303E4E",
          messageColor: "#6B7280",
          primaryButtonBg: customPrimaryButtonBg || "#3B82F6",
          primaryButtonHover: customPrimaryButtonHover || "#2563EB"
        }
      case "warning":
        return {
          iconBg: "#FEF3C7",
          iconColor: "#F59E0B",
          titleColor: "#303E4E",
          messageColor: "#6B7280",
          primaryButtonBg: customPrimaryButtonBg || "#F59E0B",
          primaryButtonHover: customPrimaryButtonHover || "#D97706"
        }
      case "error":
        return {
          iconBg: "#FEE2E2",
          iconColor: "#EF4444",
          titleColor: "#303E4E",
          messageColor: "#6B7280",
          primaryButtonBg: customPrimaryButtonBg || "#EF4444",
          primaryButtonHover: customPrimaryButtonHover || "#DC2626"
        }
      default:
        return {
          iconBg: "#D1FAE5",
          iconColor: "#10B981",
          titleColor: "#303E4E",
          messageColor: "#6B7280",
          primaryButtonBg: customPrimaryButtonBg || "#8548A8",
          primaryButtonHover: customPrimaryButtonHover || "#7A4397"
        }
    }
  }

  const variantStyles = getVariantStyles()

  const handlePrimaryAction = () => {
    if (onPrimaryAction) {
      onPrimaryAction()
    } else {
      onClose()
    }
  }

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction()
    } else {
      onClose()
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: "16px",
          padding: "32px 40px 40px 40px",
          maxWidth: "500px",
          width: "100%",
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X Button */}
        {showCloseButton && (
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "8px",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#F9FAFB"
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "transparent"
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6B7280"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}

        {/* Modal Content */}
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          {/* Icon */}
          <div
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: variantStyles.iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px auto",
              color: variantStyles.iconColor,
            }}
          >
            {getIcon()}
          </div>

          <h2
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "24px",
              fontWeight: 600,
              color: variantStyles.titleColor,
              margin: "0 0 16px 0",
            }}
          >
            {title}
          </h2>
          
          <p
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "16px",
              color: variantStyles.messageColor,
              lineHeight: "1.6",
              margin: "0 0 32px 0",
            }}
          >
            {message}
          </p>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              flexDirection: showSecondaryButton ? "column" : "row",
            }}
          >
            <button
              onClick={handlePrimaryAction}
              disabled={isLoading}
              style={{
                padding: "12px 32px",
                background: variantStyles.primaryButtonBg,
                color: "#FFFFFF",
                border: "none",
                borderRadius: "12px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "16px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                minWidth: "140px",
                opacity: isLoading ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background = variantStyles.primaryButtonHover
                  e.target.style.transform = "translateY(-1px)"
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background = variantStyles.primaryButtonBg
                  e.target.style.transform = "translateY(0)"
                }
              }}
            >
              {isLoading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid #FFFFFF",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  {loadingText}
                </div>
              ) : (
                primaryButtonText
              )}
            </button>

            {showSecondaryButton && (
              <button
                onClick={handleSecondaryAction}
                disabled={isLoading}
                style={{
                  padding: "12px 32px",
                  background: "#FFFFFF",
                  color: "#6B7280",
                  border: "1px solid #D1D5DB",
                  borderRadius: "12px",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  minWidth: "140px",
                  opacity: isLoading ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.target.style.background = "#F9FAFB"
                    e.target.style.borderColor = "#9CA3AF"
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.target.style.background = "#FFFFFF"
                    e.target.style.borderColor = "#D1D5DB"
                  }
                }}
              >
                {secondaryButtonText}
              </button>
            )}
          </div>
        </div>

        {/* Loading spinner animation */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  )
}

export default UpdateModal
